/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import en from '@/locales/en.json';

export const LOCALES = ['en', 'es', 'vi', 'zh-CN'] as const;
export type Locale = (typeof LOCALES)[number];
type Messages = Record<string, string>;
type LocaleLoader = () => Promise<{ default: Messages }>;

const localeLoaders: Record<Locale, LocaleLoader> = {
  en: async () => ({ default: en }),
  es: () => import('@/locales/es.json'),
  vi: () => import('@/locales/vi.json'),
  'zh-CN': () => import('@/locales/zh-CN.json'),
};
const loadedDictionaries: Partial<Record<Locale, Messages>> = { en };
const STORAGE_KEY = 'planet-earth-help-locale';

function isLocale(value: string | null): value is Locale {
  return Boolean(value && LOCALES.includes(value as Locale));
}

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, variables?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window === 'undefined') return 'en';
    const saved = window.localStorage?.getItem(STORAGE_KEY);
    return isLocale(saved) ? saved : 'en';
  });
  const [dictionaries, setDictionaries] = useState<Partial<Record<Locale, Messages>>>({ en });

  useEffect(() => {
    let active = true;
    if (loadedDictionaries[locale]) return undefined;

    void localeLoaders[locale]()
      .then((module) => {
        const messages = (module.default ?? module) as Messages;
        loadedDictionaries[locale] = messages;
        if (active) setDictionaries((current) => ({ ...current, [locale]: messages }));
      })
      .catch((error) => {
        console.warn(`[i18n] Could not load locale ${locale}; using English fallback`, error);
      });

    return () => {
      active = false;
    };
  }, [locale]);

  const setLocale = (nextLocale: Locale) => {
    setLocaleState(nextLocale);
    window.localStorage?.setItem(STORAGE_KEY, nextLocale);
  };

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, variables) => {
        const message = dictionaries[locale]?.[key] ?? dictionaries.en?.[key] ?? key;
        return variables
          ? message.replace(/\{(\w+)\}/g, (_, name: string) => String(variables[name] ?? `{${name}}`))
          : message;
      },
    }),
    [locale, dictionaries],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside I18nProvider');
  return context;
}
